import React, { useState, useEffect } from 'react';
import websocketManager from '../../utils/websocketManager';

/**
 * WebSocket Debug Component
 * Displays real-time WebSocket connection status and memory usage
 * Helps identify memory leaks and connection issues
 */
const WebSocketDebug = ({ userId }) => {
  const [stats, setStats] = useState({});
  const [connectionStatus, setConnectionStatus] = useState({});
  const [allStatuses, setAllStatuses] = useState({});
  const [memoryInfo, setMemoryInfo] = useState({});

  // Update stats every 2 seconds
  useEffect(() => {
    const updateStats = () => {
      setStats(websocketManager.getMemoryStats());
      
      if (userId) {
        setConnectionStatus(websocketManager.getConnectionStatus(`user_${userId}`));
      }
      
      setAllStatuses(websocketManager.getAllConnectionStatuses());

      // Get memory info if available
      if ('memory' in performance) {
        const memInfo = performance.memory;
        setMemoryInfo({
          usedJSHeapSize: (memInfo.usedJSHeapSize / 1024 / 1024).toFixed(2),
          totalJSHeapSize: (memInfo.totalJSHeapSize / 1024 / 1024).toFixed(2),
          jsHeapSizeLimit: (memInfo.jsHeapSizeLimit / 1024 / 1024).toFixed(2)
        });
      }
    };

    updateStats();
    const interval = setInterval(updateStats, 2000);
    
    return () => clearInterval(interval);
  }, [userId]);

  const handleDisconnectAll = () => {
    websocketManager.disconnectAll();
  };

  const handleForceGC = () => {
    if (window.gc) {
      window.gc();
    } else {
    }
  };

  return (
    <div className="bg-gray-900 text-white p-6 rounded-lg shadow-lg">
      <div className="flex justify-between items-center mb-4">
        <h3 className="text-xl font-bold text-green-400">🔧 WebSocket Debug Panel</h3>
        <div className="space-x-2">
          <button 
            onClick={handleDisconnectAll}
            className="px-3 py-1 bg-red-600 text-white rounded text-sm hover:bg-red-700"
          >
            Disconnect All
          </button>
          <button 
            onClick={handleForceGC}
            className="px-3 py-1 bg-blue-600 text-white rounded text-sm hover:bg-blue-700"
          >
            Force GC
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {/* Memory Stats */}
        <div className="bg-gray-800 rounded p-4">
          <h4 className="font-semibold text-blue-400 mb-2">📊 Memory Stats</h4>
          <div className="space-y-1 text-sm">
            <div>Connections: <span className="text-yellow-400">{stats.connections || 0}</span></div>
            <div>Global Listeners: <span className="text-yellow-400">{stats.globalListeners || 0}</span></div>
            <div>Reconnect Timers: <span className="text-yellow-400">{stats.reconnectTimers || 0}</span></div>
            <div>Heartbeat Intervals: <span className="text-yellow-400">{stats.heartbeatIntervals || 0}</span></div>
            <div>Total Listeners: <span className="text-yellow-400">{stats.totalListeners || 0}</span></div>
          </div>
        </div>

        {/* Current User Connection */}
        {userId && (
          <div className="bg-gray-800 rounded p-4">
            <h4 className="font-semibold text-green-400 mb-2">👤 User Connection</h4>
            <div className="space-y-1 text-sm">
              <div>User ID: <span className="text-yellow-400">{userId}</span></div>
              <div>Status: 
                <span className={`ml-1 px-2 py-1 rounded text-xs ${
                  connectionStatus.status === 'open' ? 'bg-green-600' :
                  connectionStatus.status === 'connecting' ? 'bg-yellow-600' :
                  'bg-red-600'
                }`}>
                  {connectionStatus.status || 'disconnected'}
                </span>
              </div>
              <div>Exists: <span className="text-yellow-400">{connectionStatus.exists ? 'Yes' : 'No'}</span></div>
              <div>Reconnect Attempts: <span className="text-yellow-400">{connectionStatus.reconnectAttempts || 0}</span></div>
              <div>Listeners: <span className="text-yellow-400">{connectionStatus.listeners || 0}</span></div>
              {connectionStatus.lastPong && (
                <div>Last Pong: <span className="text-yellow-400">
                  {new Date(connectionStatus.lastPong).toLocaleTimeString()}
                </span></div>
              )}
            </div>
          </div>
        )}

        {/* Browser Memory Info */}
        {Object.keys(memoryInfo).length > 0 && (
          <div className="bg-gray-800 rounded p-4">
            <h4 className="font-semibold text-purple-400 mb-2">🧠 Browser Memory</h4>
            <div className="space-y-1 text-sm">
              <div>Used: <span className="text-yellow-400">{memoryInfo.usedJSHeapSize} MB</span></div>
              <div>Total: <span className="text-yellow-400">{memoryInfo.totalJSHeapSize} MB</span></div>
              <div>Limit: <span className="text-yellow-400">{memoryInfo.jsHeapSizeLimit} MB</span></div>
              <div>Usage: 
                <span className={`ml-1 px-2 py-1 rounded text-xs ${
                  parseFloat(memoryInfo.usedJSHeapSize) > 100 ? 'bg-red-600' :
                  parseFloat(memoryInfo.usedJSHeapSize) > 50 ? 'bg-yellow-600' :
                  'bg-green-600'
                }`}>
                  {((parseFloat(memoryInfo.usedJSHeapSize) / parseFloat(memoryInfo.jsHeapSizeLimit)) * 100).toFixed(1)}%
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* All Connections */}
      {Object.keys(allStatuses).length > 0 && (
        <div className="mt-4 bg-gray-800 rounded p-4">
          <h4 className="font-semibold text-orange-400 mb-2">🔌 All Connections</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-sm">
            {Object.entries(allStatuses).map(([connectionId, status]) => (
              <div key={connectionId} className="flex justify-between items-center p-2 bg-gray-700 rounded">
                <span className="font-mono text-xs">{connectionId}</span>
                <span className={`px-2 py-1 rounded text-xs ${
                  status.status === 'open' ? 'bg-green-600' :
                  status.status === 'connecting' ? 'bg-yellow-600' :
                  'bg-red-600'
                }`}>
                  {status.status}
                </span>
                <span className="text-yellow-400">L:{status.listeners}</span>
                <span className="text-blue-400">R:{status.reconnectAttempts}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="mt-4 text-xs text-gray-400">
        <p>🔄 Updates every 2 seconds • Use browser dev tools Memory tab for detailed analysis</p>
        <p>💡 High memory usage or growing connection counts indicate memory leaks</p>
      </div>
    </div>
  );
};

export default WebSocketDebug;